// OLIVIUM · Presence — active sessions, click to inspect, revoke
// to terminate. Revoke calls /api/admin/revoke-session which is
// audited through OliviumAudit on the server side.

import { useCallback, useState } from 'react'
import SessionRow from '../../components/SessionRow'
import EmptyState from '../../components/EmptyState'
import { useSessions } from '../../hooks/useSessions'
import { api, ApiError, isAuthError } from '../../api/client'
import type { OlvSession } from '../../hooks/types'

export default function OliviumSessions() {
  const { sessions, loading, authRequired } = useSessions()
  const [revokingId, setRevokingId] = useState<string | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)

  // The realtime subscription on olv_sessions inside useSessions
  // picks up the server-side status flip after revoke; we don't
  // need an imperative refresh here.
  const handleRevoke = useCallback(async (session: OlvSession) => {
    setActionError(null)
    setRevokingId(session.id)
    try {
      await api.admin.revokeSession(session.id, 'operator_revoke_from_presence')
    } catch (err) {
      if (isAuthError(err)) {
        setActionError('Sign in as an admin to revoke sessions.')
      } else {
        setActionError(err instanceof ApiError ? `${err.code} · ${err.message}` : (err as Error).message)
      }
    } finally {
      setRevokingId(null)
    }
  }, [])

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
      <div>
        <div
          style={{
            fontSize: 11,
            color: '#7f8db4',
            fontFamily: 'var(--font-mono)',
            letterSpacing: '0.16em',
            marginBottom: 6,
          }}
        >
          OLIVIUM · PRESENCE
        </div>
        <h1
          style={{
            fontSize: 24,
            fontWeight: 700,
            margin: 0,
            color: '#edf2ff',
            fontFamily: 'var(--font-brand)',
          }}
        >
          Active Sessions
        </h1>
        <div style={{ fontSize: 13, color: '#97a7cf', marginTop: 4 }}>
          Click any session to load its full record in the right panel. Revoke terminates a session via OLIVIUM.
        </div>
        {actionError && (
          <div
            style={{
              marginTop: 10,
              padding: '8px 12px',
              borderRadius: 8,
              border: '1px solid rgba(255,80,80,0.35)',
              background: 'rgba(255,80,80,0.08)',
              color: '#ffbcbc',
              fontFamily: 'var(--font-mono)',
              fontSize: 11,
            }}
          >
            {actionError}
          </div>
        )}
      </div>

      <section
        style={{
          border: '1px solid rgba(255,255,255,0.08)',
          background: 'rgba(255,255,255,0.035)',
          borderRadius: 18,
          overflow: 'hidden',
        }}
      >
        {authRequired ? (
          <div style={{ padding: 24 }}>
            <EmptyState
              title="Sign in to view Presence"
              body="OLIVIUM Presence reads live session state and requires an authenticated operator session."
            />
          </div>
        ) : loading ? (
          <div style={{ padding: 24 }}>
            <EmptyState title="Loading sessions" body="Pulling active session state." />
          </div>
        ) : sessions.length === 0 ? (
          <div style={{ padding: 24 }}>
            <EmptyState title="No active sessions" body="The presence layer is quiet." />
          </div>
        ) : (
          sessions.map((s) => (
            <SessionRow
              key={s.id}
              session={s}
              onRevoke={handleRevoke}
              revoking={revokingId === s.id}
            />
          ))
        )}
      </section>
    </div>
  )
}
